import type { CcsBufferOutput } from "./types.js";
import type { CcsBufferRuleSet } from "./types.js";

// Plain-English CCS withholding/balancing copy. Lives in the rules package so
// every surface renders identical wording from one source — a REGULATORY
// requirement, not just DRY. This tool projects a Services Australia balancing
// outcome the agency alone determines, so the binding framing (panel ship-
// conditions + CLAUDE.md §3) is:
//   - every output is the user's OWN modelled estimate that Services Australia
//     FINALISES ("you MAY owe ~$X — Services Australia determines the final
//     amount"). Never a promise, never an industry-average dollar.
//   - the per-family "typical debt" dollar is UNVERIFIED — surfaced ONLY as a
//     qualitative "common outcome" band, never a precise/average figure.
//   - the two levers (update estimate / change withholding %) are NEUTRAL
//     options the family controls — never "you should". Point to the official
//     myGov channel; state the "up to twice a year online" limit.
//   - mandatory "Educational tool. Not financial advice." disclaimer + a
//     Services Australia source cite on every output.
// Pure string-builders, unit-tested.

/** AUD, en-AU, 2dp. */
export function formatAud(amount: number): string {
  const rounded = Math.round(amount * 100) / 100;
  return `A$${rounded.toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function pct(fraction: number): string {
  return `${+(fraction * 100).toFixed(2)}%`;
}

/** Mandatory disclaimer — rendered on every result surface. */
export const CCS_BUFFER_DISCLAIMER =
  "Educational tool. Not financial advice. This is your own modelled estimate, not a Services Australia decision — Services Australia determines the final balancing amount after your income is confirmed.";

/**
 * The "common outcome" band — the ONLY way the unverified typical-debt figure is
 * allowed to surface (qualitative, never a precise/average dollar).
 */
export const CCS_BUFFER_COMMON_OUTCOME_NOTE =
  "A small balancing debt or top-up is a common outcome at reconciliation, even for families who update promptly — hard-to-predict income items (like dividends or franking credits) move the final figure. This is general context, not a measured average.";

/**
 * Headline — frames the projection as the user's MAY-owe estimate that Services
 * Australia finalises. Never an outcome promise.
 */
export function balancingHeadline(output: CcsBufferOutput): string {
  if (output.direction === "debt") {
    return `On the income you entered, you may be heading for a balancing debt of about ${formatAud(
      output.projectedBalancingAmount,
    )} — Services Australia determines the final amount.`;
  }
  if (output.direction === "top_up") {
    const topUp = formatAud(Math.abs(output.projectedBalancingAmount));
    return `On the income you entered, you may receive a balancing top-up of about ${topUp} (including your withholding buffer released) — Services Australia determines the final amount.`;
  }
  return "On the income you entered, your modelled balancing position is roughly square — Services Australia determines the final amount.";
}

/** How the buffer was applied — states the mechanics, no advice. */
export function bufferBullet(
  output: CcsBufferOutput,
  currentWithholdingRate: number,
): string {
  const held = formatAud(output.withheldBuffer);
  return `Your current withholding of ${pct(
    currentWithholdingRate,
  )} set aside about ${held} of your subsidy over the year. ${
    output.direction === "debt"
      ? `On these figures that buffer did not fully cover the modelled overpayment, leaving the estimate above.`
      : `On these figures that buffer is released back to you at balancing.`
  }`;
}

/**
 * The debt-recovery profile — the 20% clawback, stated as a mechanic, not a
 * scare. `null`-safe: only meaningful on a debt outcome.
 */
export function recoveryBullet(
  output: CcsBufferOutput,
  rules: CcsBufferRuleSet,
): string | null {
  if (!output.recovery) return null;
  return `If a debt arises and your Child Care Subsidy is still current, Services Australia recovers it by withholding ${pct(
    rules.debtRecoveryRate,
  )} of your ongoing payments until it is cleared.`;
}

/**
 * The neutralising-withholding lever — framed NEUTRALLY as an option the family
 * controls, with the twice-a-year online limit. Never "you should".
 */
export function withholdingLeverBullet(
  output: CcsBufferOutput,
  rules: CcsBufferRuleSet,
): string | null {
  if (output.neutralisingWithholdingRate === null) return null;
  return `Families can change their withholding percentage up to ${rules.onlineWithholdingChangesPerYear} times a year online through myGov (or more often by phoning the Families line). On these figures, a withholding of about ${pct(
    output.neutralisingWithholdingRate,
  )} would have offset the modelled overpayment. You can also update your income estimate at any time. These are options you control — not a recommendation.`;
}

/** The 2-year income-confirmation window — stated as a deadline mechanic. */
export function confirmationWindowBullet(rules: CcsBufferRuleSet): string {
  return `You have ${rules.incomeConfirmationWindowYears} years after the end of the financial year to confirm your income. If you don't, Services Australia cancels your Child Care Subsidy and the amount paid becomes a debt.`;
}

/** Source-attribution line — composed from the ruleset metadata. */
export function sourceBullet(rules: CcsBufferRuleSet): string {
  return `These figures use the Services Australia Child Care Subsidy balancing and withholding rules for ${rules.fy} (last verified ${rules.sourcesVerifiedAt}). Always check servicesaustralia.gov.au or call 136 150.`;
}

/** Assemble the full result-surface copy (headline + bullets + disclaimer). */
export function explanationBullets(
  output: CcsBufferOutput,
  currentWithholdingRate: number,
  rules: CcsBufferRuleSet,
): string[] {
  const bullets: string[] = [
    balancingHeadline(output),
    bufferBullet(output, currentWithholdingRate),
  ];
  const recovery = recoveryBullet(output, rules);
  if (recovery) bullets.push(recovery);
  const lever = withholdingLeverBullet(output, rules);
  if (lever) bullets.push(lever);
  bullets.push(confirmationWindowBullet(rules));
  bullets.push(CCS_BUFFER_COMMON_OUTCOME_NOTE);
  bullets.push(sourceBullet(rules));
  bullets.push(CCS_BUFFER_DISCLAIMER);
  return bullets;
}
